import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react-lite';
import { defineMessages, useIntl, FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import classNames from 'classnames';

import renderMessage from '../functions/renderMessage';
import MessagePropType from '../definitions/MessagePropType';
import SignInModalPropTypes from '../definitions/SignInModalPropTypes';
import Sizes from '../definitions/Sizes';
import Colours from '../definitions/Colours';
import scrollToFirstInvalidField from '../functions/scrollToFirstInvalidField';
import WizardSizes from '../definitions/WizardSizes';
import useSessionStore from '../hooks/useSessionStore';
import useOpenSignInModalCallback from '../hooks/useOpenSignInModalCallback';
import IconArrow from './icons/IconArrow';
import Form from './Form';
import PrimaryButton from './PrimaryButton';
import RejectButton from './RejectButton';
import AsyncButton, { ASYNC_BUTTON_STATUS } from './AsyncButton';
import PageBehaviourButton from './PageBehaviourButton';
import Paragraph from './Paragraph';
import WarningModal from './WarningModal';
import { useModal } from './ModalProvider';


function WizardPageTemplate({
    name,
    messages = {},
    children,

    onContinue,
    onChange,
    onBack,
    onCancel,
    onCreateFormState,

    onSave,
    checkValidityOnSave = false,
    shouldIncludeHiddenValues,

    signInModalProps,

    includeIntroText = false,
    isAutoFocusEnabled = false,
    isContinueDisabled = false,

    ...otherProps
}) {
    const intl = useIntl();
    const modal = useModal();
    const sessionStore = useSessionStore();
    const openSignInModal = useOpenSignInModalCallback();

    // Save states
    const [ isSaveEnabled, setIsSaveEnabled ] = useState(true);

    const combinedMessages = {
        ...DEFAULT_MESSAGES,
        ...messages,
    };

    const shouldShowIntroText = (includeIntroText || messages.INTRO_TEXT);
    const isSaveDefined = typeof onSave === 'function';
    const isContinueDefined = typeof onContinue === 'function';
    const isBackDefined = typeof onBack === 'function';
    const isCancelDefined = typeof onCancel === 'function';

    // On mount & if auto focus is enabled
    //  we want to ensure we're at the top of the page
    useEffect(() => {
        typeof window !== 'undefined' && window.scrollTo(0, 0);
    }, []);

    async function handleSave(formState) {
        if (checkValidityOnSave) {
            formState.fields.forEach(f => f.setTouched(true));

            if (!formState.isValid) {
                scrollToFirstInvalidField();
                return false;
            }
        }

        const isSaveSuccessful = await onSave(formState.values, formState);

        setIsSaveEnabled(false);

        // We are saving the document regardless of the user being logged in
        // but are prompting them to do so anyway to easily resume their quotes
        if (!sessionStore.isLoggedIn) {
            openSignInModal({
                ...signInModalProps,
                shouldShowGoogleSignIn: true,
                // Requirement: If the user closes the modal without signing in,
                // the save button should still be enabled so that the sign in modal
                // prompt is still there.
                // This only applies if the user is not signed in
                onClose: () => setIsSaveEnabled(true),
            });
        }
        
        return isSaveSuccessful;
    }

    function handleModalRefuseClick() {
        modal.close();
    }

    function handleModalAcceptClick(values, state) {
        onCancel(values, state);
        modal.close();
    }

    // handler for the wizard’s `continue` button will open confirmation model when required
    function handleCancel(values, state) {
        modal.open({
            showBranding: false,
            isContentFullWidth: false,
            isCloseVisible: false,
            displayMode: 'lightbox',
            verticalAlignment: 'center',
            screenReaderLabel: (
                <FormattedMessage
                    id="base-ui.wizard-page-template.screen-reader-label.title"
                    defaultMessage="A dialogue box to confirm unsaved changes"
                />
            ),
            children: (
                <WarningModal
                    titleMessage={(
                        <FormattedMessage
                            id="base-ui.wizard-page-template.warning-modal.title"
                            defaultMessage="Are you sure you want to cancel changing your info?"
                        />
                    )}
                    descriptionMessage={(
                        <FormattedMessage
                            id="base-ui.wizard-page-template.warning-modal.description"
                            defaultMessage="If you cancel, your changes will not be saved                                    "
                        />
                    )}
                    confirmMessage={(
                        <FormattedMessage
                            id="base-ui.wizard-page-template.warning-modal.accept-btn.text"
                            defaultMessage="Yes, cancel"
                        />
                    )}
                    rejectMessage={(
                        <FormattedMessage
                            id="base-ui.wizard-page-template.warning-modal.reject-btn.text"
                            defaultMessage="No, stop"
                        />
                    )}

                    onConfirmClick={() => handleModalAcceptClick(values, state)}
                    onRejectClick={handleModalRefuseClick}
                />
            ),
        });
    }

    function handleOnChange(values) {
        setIsSaveEnabled(true);
        onChange?.(values);
    }

    // The title does not need a dot if the title already has punctuation
    // TODO: Standardize this in TextElement, etc.
    const titleMessage = combinedMessages.TITLE;
    const formattedTitle = typeof titleMessage === 'string' ? titleMessage : renderMessage(titleMessage, intl).children;
    const shouldShowDot = /[!?:.]$/.test(formattedTitle) === false;

    return (
        <WizardPageTemplate.Container {...otherProps}>
            <div className="page-intro-container">
                <If condition={shouldShowIntroText}>
                    <Paragraph
                        className="page-intro-text"
                        message={combinedMessages.INTRO_TEXT}
                    />
                </If>
            </div>
            <If condition={combinedMessages.TITLE}>
                <h2 className={classNames({ 'showDot': shouldShowDot }, 'page-title', 'rh-title-2xl', 'rh-mt-0', 'rh-mb-1_5')}>
                    {formattedTitle}
                </h2>
            </If>
            <If condition={combinedMessages.DESCRIPTION}>
                <Paragraph
                    className="page-description"
                    message={combinedMessages.DESCRIPTION}
                />
            </If>

            <Form
                className="page-template-form"
                onCreateFormState={onCreateFormState}
                onSubmit={onContinue}
                onChange={handleOnChange}
                isAutoFocusEnabled={isAutoFocusEnabled}
                shouldIncludeHiddenValues={shouldIncludeHiddenValues}
            >
                {(state) => (
                    <>
                        { typeof children === 'function'
                            ? children(state)
                            : children
                        }
                        <div className="glamazon-button-container rh-mt-4">
                            <div className="back-cancel-container">
                                <If condition={isBackDefined}>
                                    <PageBehaviourButton
                                        onClick={() => onBack(state.values, state)}
                                        message={combinedMessages.BACK}

                                        type="button"
                                        size="small"
                                        target="blank"
                                        iconLocation="left"
                                        iconComponent={IconArrow}
                                        data-name={`${name}-back`}
                                    />
                                </If>
                                {/* With Glamazon, we only render either back or cancel */}
                                <If condition={isCancelDefined && !isBackDefined}>
                                    <RejectButton
                                        onClick={() => handleCancel(state.values, state)}
                                        message={combinedMessages.CANCEL}

                                        type="button"
                                        size="large"
                                        variant="alternate"
                                        data-name={`${name}-cancel`}
                                    />
                                </If>
                            </div>
                            <div className="continue-save-container">
                                <If condition={isSaveDefined}>
                                    <AsyncButton
                                        onClick={async () => await handleSave(state)}
                                        disabled={!isSaveEnabled}

                                        messages={{
                                            [ASYNC_BUTTON_STATUS.READY]:
                                                combinedMessages.SAVE,
                                            [ASYNC_BUTTON_STATUS.IN_PROGRESS]:
                                                combinedMessages.SAVE_IN_PROGRESS,
                                            [ASYNC_BUTTON_STATUS.COMPLETE]:
                                                combinedMessages.SAVE_COMPLETE,
                                        }}

                                        buttonComponent="alternate"
                                        data-name={`${name}-save`}
                                    />
                                </If>
                                <If condition={isContinueDefined}>
                                    <PrimaryButton
                                        message={combinedMessages.CONTINUE}
                                        data-name={`${name}-continue`}
                                        disabled={isContinueDisabled}
                                    />
                                </If>
                            </div>
                        </div>
                    </>
                )}
            </Form>
        </WizardPageTemplate.Container>
    );
}

WizardPageTemplate.propTypes = {
    name: PropTypes.string.isRequired,
    messages: PropTypes.shape({
        TITLE: MessagePropType,
        DESCRIPTION: MessagePropType,
        CANCEL: MessagePropType,
        BACK: MessagePropType,
        SAVE: MessagePropType,
        CONTINUE: MessagePropType,
        INTRO_TEXT: MessagePropType,
    }),
    children: PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.node,
    ]).isRequired,

    onContinue: PropTypes.func,
    onChange: PropTypes.func,
    onBack: PropTypes.func,
    onCancel: PropTypes.func,
    onCreateFormState: PropTypes.func,

    onSave: PropTypes.func,
    checkValidityOnSave: PropTypes.bool,

    signInModalProps: SignInModalPropTypes,

    includeIntroText: PropTypes.bool,
    isAutoFocusEnabled: PropTypes.bool,
    isContinueDisabled: PropTypes.bool,
    shouldIncludeHiddenValues: PropTypes.bool,
};

WizardPageTemplate.Container = styled.section`

    > .page-intro-container {
        display: flex;
        justify-content: center;
        align-items: flex-end;
        min-height: ${WizardSizes.PAGE_INTRO_HEIGHT};

        > .page-intro-text {
            color: ${Colours.STONE_DARKEST};
            text-align: center;
        }
    }

    > .page-title {
        text-align: center;
    }

    > .page-description {
        text-align: center;
    }

    > .page-template-form {
        margin-top: ${Sizes.SPACING.TWO};
    }

    .glamazon-button-container {
        display: flex;
        flex-wrap: wrap-reverse;
        align-items: center;

        gap: ${Sizes.SPACING.TWO};

        > .back-cancel-container,
        > .continue-save-container {
            flex: 240px 1 0;
        }

        > .back-cancel-container {

            > button {
                width: fit-content;
            }
        }

        > .continue-save-container {
            display: flex;
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(0, 1fr));
            grid-column-gap: ${Sizes.SPACING.HALF};

            > button {
                flex-basis: 50%;
                flex-grow: 1;
            }
        }
    }
`;

const DEFAULT_MESSAGES = defineMessages({
    INTRO_TEXT: {
        id: 'base-ui.WizardPageTemplate.SurtitleDefaultText',
        defaultMessage: 'Let\'s get started!',
    },
    CONTINUE: {
        id: 'base-ui.WizardPageTemplate.DefaultContinueButtonText',
        defaultMessage: 'Continue',
    },
    BACK: {
        id: 'base-ui.WizardPageTemplate.DefaultBackButtonText',
        defaultMessage: 'Back',
    },
    CANCEL: {
        id: 'base-ui.WizardPageTemplate.DefaultCancelButtonText',
        defaultMessage: 'Cancel',
    },
    SAVE: {
        id: 'base-ui.WizardPageTemplate.DefaultSaveButtonReadyText',
        defaultMessage: 'Save',
    },
    SAVE_IN_PROGRESS: {
        id: 'base-ui.WizardPageTemplate.DefaultSaveButtonSavingText',
        defaultMessage: 'Saving...',
    },
    SAVE_COMPLETE: {
        id: 'base-ui.WizardPageTemplate.DefaultSaveButtonSavedText',
        defaultMessage: 'Saved!',
    },
});

export default observer(WizardPageTemplate);
